import React, { useState, useEffect, useRef } from "react"
import { get_ord_cocina } from "../Services"
import { LOGO_RESTAURANTE } from "../Image"
import "./cocina.css"

/* ============================================================
   Vista de Cocina (KDS) — estilo demo
   - Mantiene la misma lógica: get_ord_cocina() + polling cada 8s
   - Agrupa las órdenes por mesa (ord_mesa)
   - Campos usados del JSON:
       ord_mesa, res_nombre, ordd_cantidad,
       ordd_art_descripcion, ordd_entregado
   - Campos OPCIONALES (si los agregas a la API, se activan solos):
       ord_fecha        -> temporizador en vivo + color de urgencia
       ord_identificador-> número de orden (#2855)
       ordd_nota        -> nota del ítem (ej. "Sin cebolla")
   ============================================================ */

/* Íconos en línea (sin dependencias) */
const IconClock = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
)
const IconUser = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
)

/* Formatea milisegundos a mm:ss */
function formatElapsed(ms) {
  const totalSec = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
}

/* Nivel de urgencia según minutos transcurridos */
function getUrgency(minutes) {
  if (minutes >= 15) return "late"
  if (minutes >= 8) return "warning"
  return "fresh"
}

/* --- Tarjeta de una mesa --- */
function CocinaCard({ mesa, items, now }) {
  // Temporizador solo si la API envía ord_fecha
// Se descartan fechas no parseables o tiempos absurdos (negativos o > 24h)
  // para no mostrar valores como 92794:40.
  const fecha = mesa.ord_fecha ? new Date(mesa.ord_fecha).getTime() : null
  const rawElapsed = fecha && !Number.isNaN(fecha) ? now - fecha : null
  const MAX_MS = 24 * 60 * 60 * 1000 // 24 horas
  const elapsedMs = rawElapsed != null && rawElapsed >= 0 && rawElapsed < MAX_MS ? rawElapsed : null
  const minutes = elapsedMs != null ? elapsedMs / 60000 : 0
  const urgency = elapsedMs != null ? getUrgency(minutes) : "fresh"

  const numeroOrden = mesa.ord_identificador
    ? `#${String(mesa.ord_identificador).slice(-4)}`
    : null

  return (
    <article className={`kds-card ${urgency}`}>
      <header className="kds-card-head">
        <div className="kds-card-id">
          <div className="kds-card-titlerow">
            {numeroOrden && <span className="kds-card-num">{numeroOrden}</span>}
            <span className="kds-card-mesa">{mesa.res_nombre}</span>
          </div>
          {mesa.ord_vendedor != null && (
            <div className="kds-card-waiter">
              <IconUser />
              <span>Mesero {mesa.usr_nombre}</span>
            </div>
          )}
        </div>

        {elapsedMs != null && (
          <div className={`kds-timer ${urgency}`}>
            <IconClock />
            {formatElapsed(elapsedMs)}
          </div>
        )}
      </header>

      <ul className="kds-items">
        {items.map((item) => (
          <li key={item.ordd_id} className="kds-item">
            <span className="kds-item-qty">{Number(item.ordd_cantidad).toFixed(0)}×</span>
            <div className="kds-item-info">
              <span className="kds-item-name">{item.ordd_art_descripcion}</span>
              {item.ordd_nota ? <span className="kds-item-note">↳ {item.ordd_nota}</span> : null}
            </div>
          </li>
        ))}
      </ul>
    </article>
  )
}

export const Cocina = () => {
  const [dataCocinas, setDataCocina] = useState([])
  const [dataMesas, setDataMesas] = useState([])
  const [now, setNow] = useState(() => Date.now())
  const timeoutRef = useRef(null)

  async function fetchOrdenes() {
    const response = await get_ord_cocina()
    setDataCocina(response)

    // Mesas únicas (misma lógica original)
    const hashMesas = {}
    const mesas = response.filter((o) =>
      hashMesas[o.ord_mesa] || o.ord_mesa === null ? false : (hashMesas[o.ord_mesa] = true),
    )
    setDataMesas(mesas)

    timeoutRef.current = setTimeout(fetchOrdenes, 8000)
  }

  useEffect(() => {
    fetchOrdenes()
    return () => clearTimeout(timeoutRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reloj para el temporizador en vivo (1s)
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])

  const totalItems = dataCocinas.length

  return (
    <div className="kds">
      <header className="kds-topbar">
        <div className="kds-brand">
          {LOGO_RESTAURANTE ? (
            <img src={LOGO_RESTAURANTE || "/placeholder.svg"} className="kds-logo" alt="Logo" />
          ) : null}
          <div>
            <h1 className="kds-title">Cocina</h1>
            <p className="kds-sub">Tablero de preparación</p>
          </div>
        </div>
        <div className="kds-stats">
          <span className="kds-stat">
            <strong>{dataMesas.length}</strong> mesas
          </span>
          <span className="kds-stat">
            <strong>{totalItems}</strong> ítems
          </span>
        </div>
      </header>

      {dataMesas.length === 0 ? (
        <div className="kds-empty">
          <IconClock />
          <p>No hay órdenes en preparación.</p>
        </div>
      ) : (
        <div className="kds-board">
          {dataMesas.map((mesa) => {
            const items = dataCocinas.filter((c) => c.ord_mesa === mesa.ord_mesa)
            return <CocinaCard key={mesa.ord_mesa} mesa={mesa} items={items} now={now} />
          })}
        </div>
      )}
    </div>
  )
}

export default Cocina